// Starts a timeline <audio> with the scene and keeps it at the scene rate.
// Every write to currentTime skips or repeats sound, so the position is set
// only where a start is meant to begin, never from how far the scene got:
//   - reached by playing on  -> the in-point
//   - the playhead was moved -> that place in the clip (seekAudioSync)
//   - pause / resume         -> where the audio stopped (no write)
// The choice survives a wait for metadata. A slow start is not made up for.
//
// `deferRate` (routed elements, see audio_graph.es6): a start from a new
// position at a rate other than 1 makes the browser's pitch-preserving
// stretcher lag by up to ~200 ms of content. It does not when the start runs
// at 1x and the rate is written a moment later, so those starts do that.

export const createAudioSyncState = () => ({
  playPending: false,
  startedAt: 0,
  placed: false, // the audio is where its start is meant to begin
  startAt: null, // seek target waiting to be applied
  deferRate: false,
  cold: true, // the next start is from a new position, not a resume
  wantRate: 1,
  rateHeld: false, // playing at 1x until the timer writes wantRate
  rateToken: 0,
});

const START_WAIT_MS = 600; // longest wait for a pending play() before trying again
// 10 ms is the shortest wait measured to work; the scene runs ahead of the
// audio by this long times (rate - 1).
const RATE_HOLD_MS = 15;

// Plays at 1x now and writes the wanted rate shortly after playback really
// runs (a pending load or seek keeps `paused` false long before that). A
// pause, a new seek or a reset before then drops the write.
function holdRate(sync, el) {
  const token = ++sync.rateToken;
  sync.rateHeld = true;
  if (el.playbackRate !== 1) el.playbackRate = 1;
  const live = () => token === sync.rateToken;
  const write = () => {
    if (!live()) return;
    sync.rateHeld = false;
    if (el.paused) return; // still a cold start next time
    sync.cold = false;
    if (el.playbackRate !== sync.wantRate) el.playbackRate = sync.wantRate;
  };
  let armed = false;
  const arm = () => {
    if (armed || !live()) return;
    if (el.paused) return write(); // paused meanwhile: give the hold up
    if (el.seeking || el.readyState < 3 /* HAVE_FUTURE_DATA */) return;
    armed = true;
    setTimeout(write, RATE_HOLD_MS);
  };
  ["playing", "seeked", "canplay", "pause"].forEach((n) =>
    el.addEventListener(n, arm, { once: true })
  );
  arm();
}

const defers = (sync) => sync.deferRate && sync.wantRate !== 1;

// The scene rate changed outside a frame update (speed control). A start
// that is holding 1x keeps holding; its timer then writes the new rate.
export function setAudioSyncRate(sync, el, rate) {
  const base = Number(rate) > 0 ? Number(rate) : 1;
  sync.wantRate = base;
  if (!sync.rateHeld && el.playbackRate !== base) el.playbackRate = base;
}

// The audio left its range or was stopped: the next start is from the in-point.
export function resetAudioSync(sync) {
  sync.placed = false;
  sync.startAt = null;
  sync.cold = true;
  sync.rateHeld = false;
  sync.rateToken++;
}

// Puts the audio on its start position once it can seek.
function place(sync, el, inSec) {
  if (el.readyState < 1) return false;
  const at = sync.startAt != null ? sync.startAt : inSec;
  if (Math.abs(el.currentTime - at) > 0.001) el.currentTime = at;
  sync.startAt = null;
  sync.placed = true;
  sync.cold = true;
  return true;
}

// The user moved the playhead into the clip: line the audio up on `mediaSec`.
// Works while paused, playing or ended; a newer call replaces one that is
// still waiting for metadata.
export function seekAudioSync(sync, el, mediaSec) {
  sync.startAt = mediaSec;
  sync.placed = false;
  sync.rateHeld = false;
  sync.rateToken++;
  if (!place(sync, el, mediaSec)) {
    // No metadata yet: take the position as soon as it comes, not on the
    // next frame — a paused scene has none, and the start would then wait
    // for this seek.
    el.addEventListener(
      "loadedmetadata",
      () => {
        if (sync.startAt === mediaSec && !sync.placed) place(sync, el, mediaSec);
      },
      { once: true }
    );
    return;
  }
  // A seek under a playing audio restarts it from the new position.
  if (!el.paused && defers(sync)) holdRate(sync, el);
}

// Call on every scene frame while the audio should be playing. `targetSec`
// is where the scene is in the clip, `maxSec` the clip end (trim end),
// `inSec` its in-point (trim start).
export function driveAudioSync(sync, el, targetSec, baseRate = 1, maxSec = null, inSec = 0) {
  const base = Number(baseRate) > 0 ? Number(baseRate) : 1;
  sync.wantRate = base;
  // A deferred start stays at 1x until its timer; everything else follows at once.
  const startsDeferred = el.paused && sync.cold && defers(sync);
  if (!sync.rateHeld && !startsDeferred && el.playbackRate !== base) el.playbackRate = base;
  if (!sync.placed) {
    // Already sounding (something else started it): its place is kept.
    if (!el.paused && sync.startAt == null) sync.placed = true;
    else if (!place(sync, el, inSec)) return;
  }
  if (!el.paused || el.ended || el.seeking) return;
  if (maxSec != null && targetSec >= maxSec) return;

  const now = performance.now();
  if (sync.playPending && now - sync.startedAt < START_WAIT_MS) return;
  sync.startedAt = now;
  sync.playPending = true;
  const hold = sync.cold && defers(sync);
  if (hold && el.playbackRate !== 1) el.playbackRate = 1; // before it starts
  const settle = () => {
    sync.playPending = false;
  };
  Promise.resolve(el.play()).then(settle, settle);
  if (hold) holdRate(sync, el);
  else sync.cold = false;
}
